import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Upload, Clipboard, ScanText, Sparkles, Wand2, Plus, Trash2, Download, RotateCcw, Play, Type, Image as ImageIcon, Film, Zap, Lock } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";

const RATIO_BOX = { "16:9": "aspect-video", "9:16": "aspect-[9/16]", "1:1": "aspect-square", "4:5": "aspect-[4/5]", "4:3": "aspect-[4/3]", "3:4": "aspect-[3/4]" };
const QUICK = ["Make more cinematic", "Make more energetic", "Make premium", "Make minimal", "Sports style", "News style", "Motivational", "Make it faster", "Make it slower", "Add text animation"];

const PREVIEW_CSS = `
@keyframes ai-zoom-in{from{transform:scale(1)}to{transform:scale(1.35)}}
@keyframes ai-zoom-out{from{transform:scale(1.35)}to{transform:scale(1)}}
@keyframes ai-pan-right{from{transform:scale(1.15) translateX(-6%)}to{transform:scale(1.15) translateX(6%)}}
@keyframes ai-pan-left{from{transform:scale(1.15) translateX(6%)}to{transform:scale(1.15) translateX(-6%)}}
@keyframes ai-pan-up{from{transform:scale(1.15) translateY(6%)}to{transform:scale(1.15) translateY(-6%)}}
@keyframes ai-pan-down{from{transform:scale(1.15) translateY(-6%)}to{transform:scale(1.15) translateY(6%)}}
@keyframes ai-ken{from{transform:scale(1)}to{transform:scale(1.3) translateX(5%)}}
@keyframes ai-pulse{0%,100%{transform:scale(1.05)}50%{transform:scale(1.14)}}
@keyframes ai-fadein{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
`;
const MOTION_ANIM = { zoom_in: "ai-zoom-in", zoom_out: "ai-zoom-out", pan_right: "ai-pan-right", pan_left: "ai-pan-left", pan_up: "ai-pan-up", pan_down: "ai-pan-down", ken_burns: "ai-ken", pulse: "ai-pulse", none: "" };

let TID = 0;
const newText = (o = {}) => ({ tid: ++TID, text: "New text", font: "Anton", size: 72, color: "#FFFFFF", align: "center", y: 0.5, stroke: true, strokeColor: "#000000", animation: "fade", start: 0.2, ...o });

export default function AnimateImage() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [gate, setGate] = useState(null);
  const [image, setImage] = useState(null); // {base64,url,storagePath,w,h}
  const [ocr, setOcr] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [motion, setMotion] = useState({ effect_id: "ken_burns", intensity: 55, direction: "right", fit: "cover", duration: 5 });
  const [bgColor, setBgColor] = useState("#080808");
  const [aspect, setAspect] = useState("9:16");
  const [quality, setQuality] = useState("medium");
  const [texts, setTexts] = useState([]);
  const [assets, setAssets] = useState({ background: [], overlay: [], motion_graphic: [] });
  const [bgAssetId, setBgAssetId] = useState(null);
  const [overlaysSel, setOverlaysSel] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState(null);
  const [tplValues, setTplValues] = useState({});
  const [tplBusy, setTplBusy] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const BE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.animateConfig().then(setCfg).catch((e) => {
      const st = e?.response?.status;
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Animate Image is not available.";
      setGate({ reason: st === 402 ? "upgrade_required" : "forbidden", message: typeof msg === "string" ? msg : "Not available" });
    });
  }, []);

  useEffect(() => {
    if (!cfg) return;
    ["background", "overlay", "motion_graphic"].forEach((c) => api.animateAssets(c).then((d) => setAssets((a) => ({ ...a, [c]: d.assets || [] }))).catch(() => {}));
    if (cfg.features?.mogrt) api.animateMogrtList().then((d) => setTemplates(d.templates || [])).catch(() => {});
  }, [cfg]);

  const ingest = useCallback(async (base64) => {
    setResult(null);
    try {
      const up = await api.animateUpload(base64);
      setImage({ base64, url: `${BE}${up.url}`, storagePath: up.storage_path, w: up.width, h: up.height });
      setAspect(up.width >= up.height ? (up.width / up.height > 1.5 ? "16:9" : "4:3") : (up.height / up.width > 1.5 ? "9:16" : "4:5"));
      toast.success("Image added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add image"); }
  }, [BE]);

  const onFile = (f) => { if (!f) return; const r = new FileReader(); r.onload = () => ingest(r.result); r.readAsDataURL(f); };

  // clipboard paste
  useEffect(() => {
    if (!cfg) return;
    const h = (e) => {
      const items = (e.clipboardData || {}).items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) { const f = it.getAsFile(); if (f) { onFile(f); e.preventDefault(); return; } }
      }
      // text-only paste inside the tool = ignore (do not crash)
      if (items.length && !Array.from(items).some((i) => i.type.startsWith("image/"))) {
        if (!image) toast.message("No image found in clipboard.");
      }
    };
    window.addEventListener("paste", h);
    return () => window.removeEventListener("paste", h);
  }, [cfg, image]); // eslint-disable-line

  const runOcr = async () => {
    if (!image) return;
    setOcrBusy(true);
    try { const r = await api.animateOcr(image.base64); setOcr((r.blocks || []).map((b, i) => ({ id: i, ...b }))); if (!(r.blocks || []).length) toast.message("OCR could not detect text. Enter text manually."); }
    catch (e) { toast.error(e?.response?.data?.detail || "OCR unavailable"); setOcr([]); }
    setOcrBusy(false);
  };

  const runAI = async (text) => {
    const p = (text || prompt).trim();
    if (!p) { toast.error("Type a creative request first"); return; }
    setAiBusy(true);
    try {
      const ctx = { aspect_ratio: aspect, ocr_text: ocr ? ocr.map((b) => b.text).join(", ") : undefined };
      const r = await api.animateAssistant(p, ctx);
      if (r.image) setMotion((m) => ({ ...m, ...r.image, duration: r.duration || m.duration }));
      if (r.aspect_ratio && RATIO_BOX[r.aspect_ratio]) setAspect(r.aspect_ratio);
      if (r.background?.color) setBgColor(r.background.color);
      if (Array.isArray(r.texts) && r.texts.length) setTexts(r.texts.map((t) => newText({ ...t, size: t.size || 72 })));
      toast.success(r.summary || "Applied AI suggestions");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation is temporarily unavailable."); }
    setAiBusy(false);
  };

  const addOcrAsText = (b) => setTexts((t) => [...t, newText({ text: b.text, size: b.role === "heading" ? 90 : 56, y: 0.3 + t.length * 0.12 })]);
  const updText = (tid, patch) => setTexts((ts) => ts.map((t) => t.tid === tid ? { ...t, ...patch } : t));
  const delText = (tid) => setTexts((ts) => ts.filter((t) => t.tid !== tid));

  const doRender = async () => {
    if (!image) { toast.error("Add an image first"); return; }
    setRendering(true); setResult(null);
    const bgAsset = assets.background.find((a) => a.id === bgAssetId);
    const config = {
      image_storage_path: image.storagePath, aspect_ratio: aspect, duration: motion.duration,
      background: bgAsset ? { type: "image", asset_path: bgAsset.storage_path, color: bgColor } : { type: "solid", color: bgColor },
      image: { effect_id: motion.effect_id, intensity: motion.intensity, direction: motion.direction, fit: motion.fit, image_scale: 0.82 },
      overlays: overlaysSel.map((o) => ({ asset_path: o.storage_path, type: o.asset_type, x: 0.5, y: 0.5, scale: 1.0, opacity: 0.85, start: 0.0, blend: o.blend })),
      texts: texts.map((t) => ({ text: t.text, font: t.font, size: t.size, color: t.color, align: t.align, y: t.y, stroke: t.stroke, strokeColor: t.strokeColor, animation: t.animation, start: t.start })),
    };
    try {
      const r = await api.animateRender(config, quality, "manual");
      setResult(r.render);
      toast.success(`Render complete in ${(r.render_ms / 1000).toFixed(1)}s`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Render failed"); }
    setRendering(false);
  };

  const reset = () => { setImage(null); setOcr(null); setTexts([]); setResult(null); setPrompt(""); setOverlaysSel([]); setBgAssetId(null); setTpl(null); };

  const renderTemplate = async () => {
    if (!tpl) return;
    setTplBusy(true); setResult(null);
    try {
      const r = await api.animateMogrtRender(tpl.id, tplValues, quality);
      setResult(r.render);
      toast.success("Template rendered");
    } catch (e) { toast.error(e?.response?.data?.detail || "Template render failed"); }
    setTplBusy(false);
  };

  if (gate) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white"><Navbar />
        <div className="mx-auto flex max-w-lg flex-col items-center gap-3 px-6 py-32 text-center" data-testid="animate-gate">
          {gate.reason === "upgrade_required" ? <Zap size={34} className="text-[#00F0FF]" /> : <Lock size={34} className="text-amber-400" />}
          <h1 className="text-2xl font-black">{gate.reason === "upgrade_required" ? "Animate Image requires Pro" : "Animate Image is unavailable"}</h1>
          <p className="text-white/50">{gate.message}</p>
          {gate.reason === "upgrade_required" && <Link to="/pricing" data-testid="animate-get-pro" className="mt-2 rounded-full bg-[#00F0FF] px-5 py-2.5 text-sm font-bold text-black">Get Pro</Link>}
          <Link to="/tools" className="mt-2 text-sm text-white/50 hover:text-white">← Back to Tools</Link>
        </div>
      </div>
    );
  }
  if (!cfg) return <div className="min-h-screen bg-[#0A0A0A]"><Navbar /><div className="flex justify-center py-40"><Loader2 className="animate-spin text-[#00F0FF]" /></div></div>;

  const motionAnim = MOTION_ANIM[cfg.effects.find((e) => e.effect_id === motion.effect_id)?.motion] || "";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <style>{PREVIEW_CSS}</style>
      <Navbar />
      <div className="mx-auto max-w-[1300px] px-4 py-6 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <a href="/tools" className="mb-1 inline-flex items-center gap-1 text-xs text-white/50 hover:text-white"><ArrowLeft size={13} /> Tools</a>
            <h1 className="font-display text-3xl font-black tracking-tight">Animate Image</h1>
            <p className="text-sm text-white/45">Turn images into animated social videos with motion, text, overlays and AI.</p>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="animate-aspect" value={aspect} onChange={(e) => setAspect(e.target.value)} className="rounded-lg border border-white/15 bg-[#111] px-3 py-2 text-sm">
              {cfg.aspect_ratios.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button data-testid="animate-render-btn" disabled={!image || rendering} onClick={doRender}
              className="inline-flex items-center gap-2 rounded-full bg-[#00F0FF] px-5 py-2.5 text-sm font-bold text-black disabled:opacity-40">
              {rendering ? <><Loader2 size={16} className="animate-spin" /> Rendering…</> : <><Film size={16} /> Render</>}
            </button>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_400px]">
          {/* CANVAS */}
          <div>
            <div className={`relative mx-auto w-full max-w-[520px] overflow-hidden rounded-2xl border border-white/10 ${RATIO_BOX[aspect]}`} style={{ background: bgColor }} data-testid="animate-canvas"
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}>
              {result ? (
                <video data-testid="animate-result-video" src={`${BE}${result.mp4_url}`} controls autoPlay loop className="h-full w-full object-contain bg-black" />
              ) : image ? (
                <>
                  <img alt="src" src={image.url} className={`h-full w-full ${motion.fit === "contain" ? "object-contain" : "object-cover"}`}
                    style={motionAnim ? { animation: `${motionAnim} ${motion.duration}s ease-in-out infinite alternate` } : {}} />
                  {texts.map((t) => (
                    <div key={t.tid} className="pointer-events-none absolute left-0 right-0 px-4"
                      style={{ top: `${t.y * 100}%`, transform: "translateY(-50%)", textAlign: t.align, animation: "ai-fadein 0.6s ease-out both" }}>
                      <span style={{ fontFamily: t.font, fontSize: `${Math.max(14, t.size / 3.2)}px`, color: t.color, fontWeight: 800, WebkitTextStroke: t.stroke ? `1px ${t.strokeColor}` : undefined, textShadow: "2px 2px 6px rgba(0,0,0,.6)", lineHeight: 1.1 }}>{t.text}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                  <Upload size={30} className="text-white/40" />
                  <p className="text-sm text-white/50">Upload, drag &amp; drop, or paste an image (Ctrl/⌘+V)</p>
                  <div className="flex gap-2">
                    <button data-testid="animate-upload-btn" onClick={() => fileRef.current?.click()} className="rounded-full bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black">+ Upload Image</button>
                  </div>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => onFile(e.target.files?.[0])} />
            {result && (
              <div className="mx-auto mt-3 flex max-w-[520px] items-center gap-2" data-testid="animate-result-bar">
                <a data-testid="animate-download" href={`${BE}${result.mp4_url}`} target="_blank" rel="noreferrer" className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#00F0FF] px-4 py-2.5 text-sm font-bold text-black"><Download size={15} /> Download Video</a>
                <button data-testid="animate-new" onClick={reset} className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/80 hover:bg-white/5"><RotateCcw size={14} /> Create New</button>
              </div>
            )}
          </div>

          {/* CONTROLS */}
          <div className="space-y-3">
            {/* input row */}
            <div className="flex gap-2">
              <button data-testid="animate-input-upload" onClick={() => fileRef.current?.click()} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/15 py-2.5 text-xs font-semibold hover:bg-white/5"><Upload size={14} /> Upload</button>
              <button data-testid="animate-input-paste" onClick={() => toast.message("Copy an image, then press Ctrl/⌘+V anywhere here")} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/15 py-2.5 text-xs font-semibold hover:bg-white/5"><Clipboard size={14} /> Paste</button>
            </div>

            {/* OCR */}
            {cfg.features.ocr && (
              <Section title="OCR" icon={ScanText}>
                <button data-testid="animate-ocr-btn" disabled={!image || ocrBusy} onClick={runOcr} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-white/15 py-2 text-xs font-semibold disabled:opacity-40 hover:bg-white/5">{ocrBusy ? <Loader2 size={13} className="animate-spin" /> : <ScanText size={13} />} Extract Text from Image</button>
                {ocr && ocr.map((b) => (
                  <div key={b.id} className="mt-2 flex items-center gap-2" data-testid={`animate-ocr-block-${b.id}`}>
                    <input value={b.text} onChange={(e) => setOcr((o) => o.map((x) => x.id === b.id ? { ...x, text: e.target.value } : x))} className="flex-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-xs" />
                    <span className="text-[10px] text-white/40">{b.role}</span>
                    <button data-testid={`animate-ocr-add-${b.id}`} onClick={() => addOcrAsText(b)} className="rounded-lg bg-white/10 px-2 py-1.5 text-[11px] font-bold hover:bg-white/20">+ Text</button>
                  </div>
                ))}
              </Section>
            )}

            {/* AI Assistant / Creative Direction */}
            {cfg.features.ai && (
              <Section title="Creative Direction" icon={Sparkles}>
                <textarea data-testid="animate-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} placeholder="Create a cinematic slow zoom with a bold title, suitable for Instagram Reels…" className="w-full resize-none rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs outline-none focus:border-[#00F0FF]/50" />
                <div className="mt-2 flex gap-2">
                  <button data-testid="animate-ai-generate" disabled={aiBusy} onClick={() => runAI()} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#00F0FF] py-2 text-xs font-bold text-black disabled:opacity-50">{aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Generate</button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {QUICK.map((q) => <button key={q} data-testid={`animate-ai-chip-${q.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => runAI(q)} disabled={aiBusy} className="rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-white/70 hover:border-[#00F0FF]/50 hover:text-white disabled:opacity-40">{q}</button>)}
                </div>
              </Section>
            )}

            {/* Motion */}
            <Section title="Animate Image" icon={ImageIcon}>
              <label className="text-[11px] text-white/50">Effect</label>
              <select data-testid="animate-effect" value={motion.effect_id} onChange={(e) => setMotion((m) => ({ ...m, effect_id: e.target.value }))} className="mt-1 w-full rounded-lg border border-white/15 bg-[#111] px-2 py-2 text-xs">
                {cfg.effects.map((e) => <option key={e.effect_id} value={e.effect_id}>{e.name}</option>)}
              </select>
              <Slider testid="animate-intensity" label={`Intensity ${motion.intensity}`} min={5} max={100} value={motion.intensity} onChange={(v) => setMotion((m) => ({ ...m, intensity: v }))} />
              <Slider testid="animate-duration" label={`Duration ${motion.duration}s`} min={2} max={20} value={motion.duration} onChange={(v) => setMotion((m) => ({ ...m, duration: v }))} />
              <div className="mt-2 flex gap-2">
                <select data-testid="animate-direction" value={motion.direction} onChange={(e) => setMotion((m) => ({ ...m, direction: e.target.value }))} className="flex-1 rounded-lg border border-white/15 bg-[#111] px-2 py-1.5 text-xs">{["right", "left", "up", "down"].map((d) => <option key={d} value={d}>{d}</option>)}</select>
                <select data-testid="animate-fit" value={motion.fit} onChange={(e) => setMotion((m) => ({ ...m, fit: e.target.value }))} className="flex-1 rounded-lg border border-white/15 bg-[#111] px-2 py-1.5 text-xs">{["cover", "contain"].map((d) => <option key={d} value={d}>{d}</option>)}</select>
              </div>
            </Section>

            {/* Background */}
            <Section title="Background" icon={ImageIcon}>
              <div className="flex items-center gap-2"><span className="text-xs text-white/50">Color</span><input data-testid="animate-bg-color" type="color" value={bgColor} onChange={(e) => { setBgColor(e.target.value); setBgAssetId(null); }} className="h-8 w-14 rounded border border-white/15 bg-transparent" /></div>
              {assets.background.length > 0 && (
                <div className="mt-2 grid grid-cols-4 gap-1.5" data-testid="animate-bg-library">
                  {assets.background.map((a) => (
                    <button key={a.id} data-testid={`animate-bg-${a.id}`} title={a.name} onClick={() => setBgAssetId(bgAssetId === a.id ? null : a.id)}
                      className={`overflow-hidden rounded-md border ${bgAssetId === a.id ? "border-[#00F0FF]" : "border-white/10"}`}>
                      <img src={`${BE}/api/media?path=${encodeURIComponent(a.preview_path)}`} alt={a.name} className="aspect-square w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Section>

            {/* Overlays + Motion Graphics */}
            {(assets.overlay.length > 0 || assets.motion_graphic.length > 0) && (
              <Section title="Overlays & Motion Graphics" icon={Film}>
                <div className="grid grid-cols-4 gap-1.5" data-testid="animate-overlay-library">
                  {[...assets.overlay, ...assets.motion_graphic].map((a) => {
                    const on = overlaysSel.some((o) => o.id === a.id);
                    return (
                      <button key={a.id} data-testid={`animate-overlay-${a.id}`} title={a.name} onClick={() => setOverlaysSel((s) => on ? s.filter((o) => o.id !== a.id) : [...s, a])}
                        className={`overflow-hidden rounded-md border ${on ? "border-[#00F0FF] ring-1 ring-[#00F0FF]" : "border-white/10"}`}>
                        <img src={`${BE}/api/media?path=${encodeURIComponent(a.preview_path)}`} alt={a.name} className="aspect-square w-full bg-black/40 object-cover" />
                      </button>
                    );
                  })}
                </div>
                {overlaysSel.length > 0 && <div className="mt-1.5 text-[10px] text-white/40">{overlaysSel.length} overlay(s) selected — tap to toggle.</div>}
              </Section>
            )}

            {/* Text layers */}
            <Section title="Animate Text" icon={Type}>
              <button data-testid="animate-add-text" onClick={() => setTexts((t) => [...t, newText()])} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-white/15 py-2 text-xs font-semibold hover:bg-white/5"><Plus size={13} /> Add Text</button>
              {texts.map((t) => (
                <div key={t.tid} className="mt-2 space-y-1.5 rounded-lg border border-white/10 bg-white/[0.02] p-2" data-testid={`animate-text-${t.tid}`}>
                  <div className="flex items-center gap-2">
                    <input data-testid={`animate-text-input-${t.tid}`} value={t.text} onChange={(e) => updText(t.tid, { text: e.target.value })} className="flex-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-xs" />
                    <button data-testid={`animate-text-del-${t.tid}`} onClick={() => delText(t.tid)} className="rounded border border-white/15 p-1.5 text-red-400 hover:bg-white/5"><Trash2 size={12} /></button>
                  </div>
                  <div className="flex gap-1.5">
                    <select value={t.font} onChange={(e) => updText(t.tid, { font: e.target.value })} className="min-w-0 flex-1 rounded border border-white/15 bg-[#111] px-1.5 py-1 text-[11px]">{cfg.fonts.map((f) => <option key={f} value={f}>{f}</option>)}</select>
                    <select value={t.animation} onChange={(e) => updText(t.tid, { animation: e.target.value })} className="rounded border border-white/15 bg-[#111] px-1.5 py-1 text-[11px]">{cfg.text_animations.map((a) => <option key={a} value={a}>{a}</option>)}</select>
                    <input type="color" value={t.color} onChange={(e) => updText(t.tid, { color: e.target.value })} className="h-7 w-8 rounded border border-white/15 bg-transparent" />
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-white/50">
                    <span>Size</span><input type="range" min={24} max={200} value={t.size} onChange={(e) => updText(t.tid, { size: +e.target.value })} className="flex-1" />
                    <span>Y</span><input type="range" min={0} max={100} value={t.y * 100} onChange={(e) => updText(t.tid, { y: +e.target.value / 100 })} className="flex-1" />
                  </div>
                </div>
              ))}
            </Section>

            {/* Output */}
            <Section title="Output" icon={Film}>
              <label className="text-[11px] text-white/50">Quality</label>
              <select data-testid="animate-quality" value={quality} onChange={(e) => setQuality(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-[#111] px-2 py-2 text-xs">{cfg.quality_options.map((q) => <option key={q} value={q}>{q}</option>)}</select>
            </Section>

            {/* Motion Templates (MOGRT) */}
            {cfg.features?.mogrt && (
              <Section title="Motion Templates" icon={Film}>
                {templates.length === 0 ? (
                  <p className="text-[11px] text-white/40" data-testid="animate-mogrt-empty">No live templates yet. Admins publish these from the MOGRT pipeline.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="animate-mogrt-list">
                    {templates.map((t) => (
                      <button key={t.id} data-testid={`animate-mogrt-${t.id}`} onClick={() => { setTpl(t); const v = {}; (t.schema?.fields || []).forEach((f) => { v[f.key] = f.default || ""; }); setTplValues(v); }}
                        className={`w-full rounded-lg border px-3 py-2 text-left text-xs font-semibold ${tpl?.id === t.id ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/80 hover:bg-white/5"}`}>{t.template_name} <span className="text-[10px] text-white/40">v{t.version}</span></button>
                    ))}
                  </div>
                )}
                {tpl && (
                  <div className="mt-2 space-y-1.5 rounded-lg border border-white/10 bg-white/[0.02] p-2" data-testid="animate-mogrt-form">
                    {(tpl.schema?.fields || []).filter((f) => ["text", "number"].includes(f.type)).map((f) => (
                      <div key={f.key}>
                        <label className="text-[10px] text-white/50">{f.label}</label>
                        <input data-testid={`animate-mogrt-field-${f.key}`} value={tplValues[f.key] || ""} onChange={(e) => setTplValues((v) => ({ ...v, [f.key]: e.target.value }))} className="mt-0.5 w-full rounded border border-white/10 bg-white/5 px-2 py-1 text-xs" />
                      </div>
                    ))}
                    <button data-testid="animate-mogrt-render" disabled={tplBusy} onClick={renderTemplate} className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#00F0FF] py-2 text-xs font-bold text-black disabled:opacity-50">{tplBusy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Render Template</button>
                  </div>
                )}
              </Section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
    <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-white/60"><Icon size={13} /> {title}</div>
    {children}
  </div>
);

const Slider = ({ testid, label, min, max, value, onChange }) => (
  <div className="mt-2">
    <div className="mb-1 text-[11px] text-white/50">{label}</div>
    <input data-testid={testid} type="range" min={min} max={max} value={value} onChange={(e) => onChange(+e.target.value)} className="w-full" />
  </div>
);
